'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import { useHasTouchInput } from '@repo/ui';
import { pluralize } from '@repo/utils';
import { type KeyboardEvent, memo, type ReactNode, useCallback, useState } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger, Popover, PopoverContent, PopoverTrigger } from '@/components/common/overlays';
import WarningHoverCardContent, { type Warning } from './WarningHoverCardContent';
export interface WarningHoverCardProps extends PropsWithStyles {
  warnings: Warning[];
  children: ReactNode;
  isSimple: boolean;
}
const WarningHoverCard = t0 => {
  const $ = _c(30);
  const {
    warnings,
    children,
    isSimple
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const hasTouchInput = useHasTouchInput();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsOpen(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleClose = t1;
  if (warnings.length === 0) {
    return null;
  }
  let t2;
  if ($[1] !== warnings.length) {
    t2 = pluralize(warnings.length, "warning");
    $[1] = warnings.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `Show ${t2}`;
  let t4;
  if ($[3] !== isOpen || $[4] !== t3) {
    t4 = {
      role: "button",
      tabIndex: 0,
      "aria-expanded": isOpen,
      "aria-label": t3,
      className: "focus-visible:ring-ring/50 rounded-sm outline-none focus-visible:ring-[3px]",
      onKeyDown: _temp
    };
    $[3] = isOpen;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const triggerProps = t4;
  if (hasTouchInput) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = e_0 => {
        e_0.preventDefault();
        e_0.stopPropagation();
        setIsOpen(_temp2);
      };
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== children || $[8] !== triggerProps) {
      t6 = <PopoverTrigger asChild={true}><div {...triggerProps} onClick={t5}>{children}</div></PopoverTrigger>;
      $[7] = children;
      $[8] = triggerProps;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        padding: 0,
        outline: "none"
      };
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== isSimple || $[12] !== warnings) {
      t8 = <PopoverContent side="top" sideOffset={8} style={t7} onClick={_temp3}><WarningHoverCardContent warnings={warnings} isSimple={isSimple} onClose={handleClose} /></PopoverContent>;
      $[11] = isSimple;
      $[12] = warnings;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== isOpen || $[15] !== t6 || $[16] !== t8) {
      t9 = <Popover open={isOpen} onOpenChange={setIsOpen}>{t6}{t8}</Popover>;
      $[14] = isOpen;
      $[15] = t6;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      setIsOpen(true);
    };
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== children || $[20] !== triggerProps) {
    t6 = <HoverCardTrigger asChild={true}><div {...triggerProps} onClick={t5}>{children}</div></HoverCardTrigger>;
    $[19] = children;
    $[20] = triggerProps;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      minWidth: "275px",
      maxWidth: "315px"
    };
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== isSimple || $[24] !== warnings) {
    t8 = <HoverCardContent style={t7} side="top" sideOffset={8} onClick={_temp4}><WarningHoverCardContent warnings={warnings} isSimple={isSimple} onClose={handleClose} /></HoverCardContent>;
    $[23] = isSimple;
    $[24] = warnings;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== isOpen || $[27] !== t6 || $[28] !== t8) {
    t9 = <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={200} closeDelay={100}>{t6}{t8}</HoverCard>;
    $[26] = isOpen;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
export default memo(WarningHoverCard);
function _temp(e) {
  if (e.repeat || e.key !== "Enter" && e.key !== " ") {
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  e.currentTarget.click();
}
function _temp2(prev) {
  return !prev;
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e_3) {
  return e_3.stopPropagation();
}